import { type ProjectParticipant } from '@consigli/types';
import { FC, useCallback, useMemo } from 'react';

import { type ActionItemColumn, type GroupByOption, type SupportedItem } from './action-item-types';
import { ActionItemGroupHeader } from './action-items-group-header';
import { ActionItemTableHeader } from './action-items-table-header';
import { ActionItemTableRow } from './action-items-table-row';
import {
  formatDateToEuropean,
  formatStatusToTitleCase,
  getStatusBadgeClass,
} from './action-items-table-utils';
import { BulkSelectionBar } from './bulk-selection-bar';
import { useGroupedItems } from '../hooks/use-grouped-items';
import { useItemSelection } from '../hooks/use-item-selection';
import { collectCustomParties } from '../utils/participant-utils';

// Re-export types and utilities for external use
export type { ActionItemColumn, GroupByOption };
export {
  formatDateToEuropean as formatDate,
  formatStatusToTitleCase as formatStatusLabel,
  getStatusBadgeClass,
};

interface ActionItemsTableProps {
  items: SupportedItem[];
  columns: ActionItemColumn[];
  groupBy: GroupByOption;
  participants: ProjectParticipant[];
  topics: string[];
  meetingId?: string;
  expandedGroups: Set<string>;
  onToggleGroup: (groupKey: string) => void;
  onUpdate?: (item: SupportedItem) => void;
  onDelete: (item: SupportedItem) => void;
  onAddCustomTopic?: (topic: string) => void;
  onShowSource?: (item: SupportedItem) => void;
  onBulkReassignItems?: (
    itemIds: string[],
    target: { toMeetingUserId?: string | null; toCustomPartyName?: string | null },
  ) => Promise<void>;
  onBulkUpdateStatus?: (itemIds: string[], status: string) => Promise<void>;
  onBulkUpdateDeadline?: (itemIds: string[], deadline: string | null) => Promise<void>;
  onBulkAddTopic?: (itemIds: string[], topic: string) => Promise<void>;
  getGroupKey?: (item: SupportedItem, groupBy: GroupByOption) => string;
}

export const ActionItemsTable: FC<ActionItemsTableProps> = ({
  items,
  columns,
  groupBy,
  participants,
  topics,
  meetingId,
  expandedGroups,
  onToggleGroup,
  onUpdate,
  onDelete,
  onAddCustomTopic,
  onShowSource,
  onBulkReassignItems,
  onBulkUpdateStatus,
  onBulkUpdateDeadline,
  onBulkAddTopic,
  getGroupKey: customGetGroupKey,
}) => {
  // Use the selection hook for managing checkbox selection state
  const {
    selectedItems,
    toggleItem,
    toggleGroup,
    isGroupAllSelected,
    isGroupPartiallySelected,
    clearSelection,
  } = useItemSelection();

  // Use the grouped items hook for grouping and sorting logic
  const { sortedGroups } = useGroupedItems({
    items,
    groupBy,
    getGroupKey: customGetGroupKey,
  });

  // Handle bulk reassign for selected items
  const handleBulkReassign = useCallback(
    async (
      itemIds: string[],
      target: { toMeetingUserId?: string | null; toCustomPartyName?: string | null },
    ) => {
      if (!onBulkReassignItems || selectedItems.size === 0) return;
      await onBulkReassignItems(itemIds, target);
      clearSelection();
    },
    [onBulkReassignItems, selectedItems, clearSelection],
  );

  // Handle bulk status update for selected items
  const handleBulkStatusUpdate = useCallback(
    async (itemIds: string[], status: string) => {
      if (!onBulkUpdateStatus || selectedItems.size === 0) return;
      await onBulkUpdateStatus(itemIds, status);
      clearSelection();
    },
    [onBulkUpdateStatus, selectedItems, clearSelection],
  );

  // Handle bulk deadline update for selected items
  const handleBulkDeadlineUpdate = useCallback(
    async (itemIds: string[], deadline: string | null) => {
      if (!onBulkUpdateDeadline || selectedItems.size === 0) return;
      await onBulkUpdateDeadline(itemIds, deadline);
      clearSelection();
    },
    [onBulkUpdateDeadline, selectedItems, clearSelection],
  );

  // Handle bulk topic addition for selected items
  const handleBulkTopicAdd = useCallback(
    async (itemIds: string[], topic: string) => {
      if (!onBulkAddTopic || selectedItems.size === 0) return;
      await onBulkAddTopic(itemIds, topic);
      clearSelection();
    },
    [onBulkAddTopic, selectedItems, clearSelection],
  );

  // Filter participants to exclude the current assignee(s) of selected items,
  // avoiding no-op reassignments. If items from multiple groups are selected,
  // all their assignees are excluded; users can reassign in multiple steps.
  const availableParticipants = useMemo(() => {
    if (selectedItems.size === 0) return participants;

    const selectedAssigneeIds = new Set<string>();
    items.forEach((item) => {
      const userId = item.responsiblePerson?.meetingUserId;
      if (item.id && selectedItems.has(item.id) && userId) {
        selectedAssigneeIds.add(userId);
      }
    });

    return participants.filter((p) => !selectedAssigneeIds.has(p.id));
  }, [selectedItems, items, participants]);

  const customParties = useMemo(
    () => collectCustomParties(items.map((item) => item.responsiblePerson)),
    [items],
  );

  // Row/group selection is available whenever ANY bulk action is wired up, so the
  // checkboxes stay in sync with the bulk selection bar's own visibility condition.
  const bulkActionsEnabled = Boolean(
    onBulkReassignItems || onBulkUpdateStatus || onBulkUpdateDeadline || onBulkAddTopic,
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-visible relative">
      {/* Bulk selection bar - overlays header when items are selected */}
      {bulkActionsEnabled && selectedItems.size > 0 && (
        <BulkSelectionBar
          selectedCount={selectedItems.size}
          selectedItemIds={Array.from(selectedItems)}
          participants={availableParticipants}
          customParties={customParties}
          topics={topics}
          onReassign={handleBulkReassign}
          onUpdateStatus={handleBulkStatusUpdate}
          onUpdateDeadline={handleBulkDeadlineUpdate}
          onAddTopic={handleBulkTopicAdd}
          onClearSelection={clearSelection}
        />
      )}
      <ActionItemTableHeader columns={columns} groupBy={groupBy} />
      <div className="divide-y divide-gray-200">
        {sortedGroups.map(([groupKey, group]) => {
          const groupItemIds = group.items.map((item) => item.id).filter(Boolean) as string[];
          const allSelected = isGroupAllSelected(groupItemIds);
          const partiallySelected = isGroupPartiallySelected(groupItemIds);

          return (
            <div key={groupKey}>
              <ActionItemGroupHeader
                groupKey={groupKey}
                displayName={group.displayName}
                subtitle={group.subtitle}
                groupBy={groupBy}
                itemCount={group.items.length}
                isExpanded={expandedGroups.has(groupKey)}
                onToggle={() => onToggleGroup(groupKey)}
                isSelected={allSelected}
                isPartiallySelected={partiallySelected}
                onSelectAll={bulkActionsEnabled ? () => toggleGroup(groupItemIds) : undefined}
              />
              {expandedGroups.has(groupKey) && (
                <div className="divide-y divide-gray-100">
                  {group.items.map((item) => (
                    <ActionItemTableRow
                      key={item.id}
                      item={item}
                      columns={columns}
                      groupBy={groupBy}
                      participants={participants}
                      customParties={customParties}
                      topics={topics}
                      meetingId={meetingId}
                      onUpdate={onUpdate}
                      onDelete={onDelete}
                      onAddCustomTopic={onAddCustomTopic}
                      onShowSource={onShowSource}
                      isSelected={item.id ? selectedItems.has(item.id) : false}
                      onToggleSelect={
                        bulkActionsEnabled && item.id ? () => toggleItem(item.id!) : undefined
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
